import React from 'react';

import PropTypes from 'prop-types';

import { useUser } from '@adsk/alloy-provider';
import { Text } from '@adsk/alloy-react-box';
import { useLocale } from '@adsk/alloy-react-locale';
import theme from '@adsk/alloy-react-theme';
import Tooltip, { OverflowTooltip } from '@adsk/alloy-react-tooltip';

import { IN_PROGRESS_STEP, REVIEWER_ASSIGNEE_TYPE } from '../../consts';
import {
  useUsersData,
  useCompaniesData,
  useRolesData,
  useEntitiesData,
  useEntityData,
} from '../../hooks';
import { ReviewStep } from '../../types';
import { sortTasksByRequiredAndAlphabeticallyAllTypes } from '../../utils/steps';

export enum SIZES {
  SMALL = 'small',
  MEDIUM = 'medium',
}

interface ReviewersCellProps {
  reviewSteps: ReviewStep[];
  stacked?: boolean;
  size?: SIZES;
}

const ReviewersDisplay: React.FC<ReviewersCellProps> = ({
  reviewSteps,
  stacked,
  size,
}) => {
  const { T } = useLocale(({ locale }) => require(`../../i18n/${locale}.json`));
  const reviewerIds: string[] = [];
  const reviewerCompaniesIds: string[] = [];
  const reviewerRolesIds: string[] = [];
  const sortedReviewerIds: string[] = [];
  reviewSteps.forEach((step) => {
    const tasks = step.tasks;
    tasks.forEach((task) => {
      if (task.assignedToType === REVIEWER_ASSIGNEE_TYPE.user) {
        reviewerIds.push(task.assignedTo);
      } else if (task.assignedToType === REVIEWER_ASSIGNEE_TYPE.company) {
        reviewerCompaniesIds.push(task.assignedTo);
      } else {
        reviewerRolesIds.push(task.assignedTo);
      }
    });
  });
  const reviewerUsersData = useUsersData(reviewerIds);
  const reviewerCompaniesData = useCompaniesData(reviewerCompaniesIds);
  const reviewerRolesData = useRolesData(reviewerRolesIds);

  // to display reviewers of current step first
  if (reviewSteps) {
    const inProgressStep = reviewSteps.find((step) => {
      if (step.status === IN_PROGRESS_STEP) return step;
      return undefined;
    });
    if (inProgressStep) {
      const sortedTasksAtInProgressStep =
        sortTasksByRequiredAndAlphabeticallyAllTypes(
          inProgressStep,
          reviewerUsersData,
          reviewerCompaniesData,
          reviewerRolesData,
        );
      sortedTasksAtInProgressStep.forEach((task) => {
        sortedReviewerIds.push(task.assignedTo);
      });
    }
  }

  reviewSteps.forEach((step) => {
    if (step.status !== IN_PROGRESS_STEP) {
      const sortedTasks = sortTasksByRequiredAndAlphabeticallyAllTypes(
        step,
        reviewerUsersData,
        reviewerCompaniesData,
        reviewerRolesData,
      );
      sortedTasks.forEach((task) => {
        sortedReviewerIds.push(task.assignedTo);
      });
    }
  });
  const finalReviewerIds = new Set();
  sortedReviewerIds.forEach((id) => {
    finalReviewerIds.add(id);
  });
  const reviewersArray: string[] = Array.from(finalReviewerIds) as string[];
  const reviewers = useEntitiesData(reviewersArray);

  const { user } = useUser();
  const currentUserId = user?.autodeskId || '';
  const currentUser = useEntityData(currentUserId);
  const currentUserCompanyId = currentUser.company || '';
  const currentUserRoleIds = currentUser.roles || [];

  const userRoles = reviewersArray.filter((value) =>
    currentUserRoleIds.includes(value),
  );
  const currentUserRoleId = userRoles[0] || '';
  let mainUserId = reviewersArray[0];
  if (reviewersArray.includes(currentUserId)) {
    mainUserId = currentUserId;
  } else if (reviewersArray.includes(currentUserRoleId)) {
    mainUserId = currentUserRoleId;
  } else if (reviewersArray.includes(currentUserCompanyId)) {
    mainUserId = currentUserCompanyId;
  }
  const mainUser = useEntityData(mainUserId);
  const sizeStyle =
    size === SIZES.SMALL
      ? theme.typography.bodySmall
      : theme.typography.bodyMedium;

  const mainUserCssStyle =
    reviewerIds.includes(currentUserId) ||
    reviewersArray.includes(currentUserCompanyId) ||
    reviewersArray.includes(currentUserRoleId)
      ? { ...sizeStyle, backgroundColor: theme.colors.adskBlue100 }
      : sizeStyle;

  if (reviewersArray.length === 0) {
    return <span>-</span>;
  }
  if (!mainUser.name) {
    if (mainUser.isLoading) {
      return <span>{T.translate('LOADING')}</span>;
    } else {
      return <Text color="charcoal500">{T.translate('FORMER_MEMBER')}</Text>;
    }
  }

  const { name, property } = mainUser;

  const tooltipContent =
    reviewers.length > 1 ? (
      <>
        {reviewers.map((reviewer) => (
          <div key={reviewer.id}>
            {reviewer.property
              ? `${reviewer.name}(${reviewer.property})`
              : reviewer.name}
          </div>
        ))}
      </>
    ) : (
      <div>
        <div>{name}</div>
        <div>{property}</div>
      </div>
    );
  return (
    <Tooltip content={tooltipContent} trigger={Tooltip.TRIGGER_EVENTS.HOVER}>
      <OverflowTooltip autoTipOnEllipsis>
        <Text style={mainUserCssStyle}>{name}</Text>
        {reviewersArray.length >= 2 ? (
          stacked ? (
            <>
              <br />
              <Text style={sizeStyle}>
                {T.translate('MORE_USERS', {
                  count: reviewersArray.length - 1,
                })}
              </Text>
            </>
          ) : (
            <Text style={sizeStyle}>
              {T.translate('MORE_USERS', {
                count: reviewersArray.length - 1,
              })}
            </Text>
          )
        ) : (
          property &&
          (stacked ? (
            <>
              <br />
              <Text color="charcoal700" style={sizeStyle}>
                {property}
              </Text>
            </>
          ) : (
            <Text marginLeft="4px" style={sizeStyle}>
              ({property})
            </Text>
          ))
        )}
      </OverflowTooltip>
    </Tooltip>
  );
};

ReviewersDisplay.displayName = 'SubmittalsReviewersDisplay';
ReviewersDisplay.propTypes = {
  reviewSteps: PropTypes.array.isRequired,
  size: PropTypes.oneOf(Object.values(SIZES)).isRequired,
  stacked: PropTypes.bool,
};
ReviewersDisplay.defaultProps = {
  stacked: false,
  size: SIZES.MEDIUM,
};

export default ReviewersDisplay;
